import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core"
import { ResourceDetailsService } from "../../../services/resource-details.service"
import { ResourceService } from "../../../services/resource.service"
import { Asset, ContentFlags } from "../../../models/resource/asset"
import { Resource } from "../../../models/resource/resource"
import { Media, TranscriptMedia } from "../../../models/resource/media"
import { MuxDataModel } from "../../../models/muxData.model"
import { GtmVideoJsInfo } from "../../../utils/types"
import { STATIC_IMAGES } from "../../../models/constants"
import { AddOnViewService } from "../../../services/add-on-view.service"
import { Observable, Subject } from "rxjs"
import { takeUntil, tap } from "rxjs/operators"
import { CautionFlagsService } from "../../../services/caution-flags.service"
import { IndexedDBService } from "../../../services/index-DB.service"

@Component({
    selector: "app-media-gallery",
    templateUrl: "./media-gallery.component.html",
    styleUrls: ["./media-gallery.component.scss"],
    standalone: false,
})
export class MediaGalleryComponent implements OnInit {
    private audioPlayer = null
    @Input() resource: Resource
    isAudioLoading: boolean = false
    isAudioFirstStart: boolean = false
    isAudioPlaying: boolean = false
    muxData: MuxDataModel
    placeholderImg: string = STATIC_IMAGES.placeholder_image
    selectedMedia: Media = null
    gtmVideoJsInfo: GtmVideoJsInfo
    isAddonView: boolean
    isCautionFlagContainerDisplayed: boolean
    cautionFlagButtonName: string = "Watch Video"

    private destroyed$: Subject<void> = new Subject<void>()

    cautionFlagDisplayObservable: Observable<boolean> =
        this.cautionFlagsService.cautionFlagDisplayObservable.pipe(
            tap((nextState: boolean) => (this.isCautionFlagContainerDisplayed = nextState)),
            takeUntil(this.destroyed$)
        )

    constructor(
        public resourceService: ResourceService,
        private resourceDetailsService: ResourceDetailsService,
        private cdRef: ChangeDetectorRef,
        private addOnViewService: AddOnViewService,
        private cautionFlagsService: CautionFlagsService,
        private indexedDBService: IndexedDBService
    ) {}

    ngOnInit() {
        this.isAddonView = this.addOnViewService.isAddonView
        this.cautionFlagDisplayObservable.subscribe()
        this.updateResourceProps()
    }

    public updateResourceProps() {
        this.muxData = this.resource.getMuxData()
        this.gtmVideoJsInfo = {
            title: this.resource.selectedAsset.title,
            guid: this.resource.selectedAsset.guid,
            type: this.resource.selectedAsset.mediaType,
        }
        this.selectedMedia = this.resource.getSelectedMedia()
        if (!this.resource.selectedAsset.selectedMedia?.usedFile?.hasGeoRestriction) {
            this.checkAndSetWarningBoxDisplayState(this.resource.selectedAsset)
        }
    }

    public selectAsset(galleryItem: Asset) {
        this.resource.selectAsset(galleryItem)
        this.updateResourceProps()
        this.defineButtonName(galleryItem.mediaType)
    }

    checkAndSetWarningBoxDisplayState(galleryItem: Asset) {
        if (!galleryItem.contentFlags.length || !this.assetContainsNewCautionFlags(galleryItem)) {
            this.cautionFlagsService.cautionFlagDisplay = false
            return
        }
        let resourceURL: string = this.resource.resourceCode + "/" + galleryItem.guid
        this.indexedDBService
            .getDataForMediaGalleries()
            .pipe(
                takeUntil(this.destroyed$),
                tap((data: []) => {
                    this.cautionFlagsService.isResourceCautionFlagSkipped(data, resourceURL)
                    const element: HTMLElement = document.getElementById("nav-tab")
                    if (
                        element &&
                        this.resourceService.shouldShowCautionTab(
                            this.resource.shouldShowContentWarnings
                        )
                    ) {
                        element.scrollIntoView({ behavior: "smooth" })
                        this.resourceService.deleteActiveTab()
                    }
                })
            )
            .subscribe()
    }

    public changeLanguage(event) {
        this.resource.selectedAsset.selectLanguage(event.language)
        this.updateResourceProps()
    }

    public download(event) {
        event.preventDefault()
        this.resourceDetailsService.showDownloadAgreement(
            this.resource?.selectedAsset?.selectedMedia
        )
    }

    public onGalleryItemClick(galleryItem: Asset, event, keyboardNavigation: boolean = false) {
        if (
            (event.type === "keydown" && (event.key === "Enter" || event.key === " ")) ||
            !keyboardNavigation
        ) {
            this.selectAsset(galleryItem)
            event.preventDefault()
        }
    }

    public onAudioPlayerReady(audioPlayer) {
        this.audioPlayer = audioPlayer

        this.audioPlayer.on("buffer", () => {
            this.isAudioLoading = true
            this.isAudioPlaying = false
            this.cdRef.detectChanges()
        })

        this.audioPlayer.on("playAttempt", () => {
            this.isAudioFirstStart = true
            this.isAudioLoading = false
            this.cdRef.detectChanges()
        })

        this.audioPlayer.on("play", () => {
            this.isAudioPlaying = true
            this.isAudioLoading = false
            this.cdRef.detectChanges()
        })

        this.audioPlayer.on("bufferChange", () => {
            if (!this.isAudioPlaying && this.isAudioFirstStart) {
                this.isAudioLoading = true
                this.cdRef.detectChanges()
            }
        })
    }

    public getSelectedAssetIndex() {
        return this.resource.assets.indexOf(this.resource.selectedAsset)
    }

    public previewLeft() {
        const selectedItemIdx: number = this.getSelectedAssetIndex()
        if (selectedItemIdx > 0) {
            const previousItem: Asset = this.resource.assets[selectedItemIdx - 1]
            this.selectAsset(previousItem)
        }
        const backBtn = document.querySelector(".preview-button.preview-left")
        if (this.getSelectedAssetIndex() === 0) {
            ;(backBtn as HTMLElement).style.display = "none"
        }
    }

    assetContainsNewCautionFlags(asset: Asset): boolean {
        return asset.contentFlags.some((contentFlag: ContentFlags) => contentFlag.description)
    }

    defineButtonName(mediaType: string) {
        if (mediaType !== "Audio") return
        this.cautionFlagButtonName = "Listen"
    }

    public previewRight() {
        const selectedItemIdx = this.getSelectedAssetIndex()
        if (selectedItemIdx < this.resource.assets.length - 1) {
            const nextItem = this.resource.assets[selectedItemIdx + 1]
            this.selectAsset(nextItem)
        }
        const forwardBtn = document.querySelector(".preview-button.preview-right")
        if (this.getSelectedAssetIndex() === this.resource.assets.length - 1) {
            ;(forwardBtn as HTMLElement).style.display = "none"
        }
    }

    public onGalleryItemHover() {
        const backBtn = document.querySelector(".preview-button.preview-left")
        const forwardBtn = document.querySelector(".preview-button.preview-right")
        if (document.querySelector(".jw-related-close") !== null) {
            ;(backBtn as HTMLElement).style.display = "none"
            ;(forwardBtn as HTMLElement).style.display = "none"
        } else {
            if (window.screen.width >= 1200) {
                const index = this.getSelectedAssetIndex()
                if (index !== this.resource.assets.length - 1) {
                    ;(forwardBtn as HTMLElement).style.display = "block"
                }
                if (index !== 0) {
                    ;(backBtn as HTMLElement).style.display = "block"
                }
            }
        }
    }

    public onGalleryItemMouseLeave() {
        ;(document.querySelector(".preview-button.preview-left") as HTMLElement).style.display =
            "none"
        ;(document.querySelector(".preview-button.preview-right") as HTMLElement).style.display =
            "none"
    }

    public downloadTranscript(event) {
        event.preventDefault()
        this.resourceService.downloadTranscript(<TranscriptMedia>this.resource.getSelectedMedia())
    }

    onDestroy() {
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
